// src/app/services/tour-helpers.ts
//
// Pure helpers for the tour widget. Builds a lookup map from per-service
// travel_times arrays and computes route durations.

import {
  TourEligibleServiceDao,
} from '../db-models/tour-dao';

export type TravelTimeMap = Map<string, number>;

/**
 * Build a lookup map keyed by "${fromId}-${toId}" from the travel_times
 * arrays attached to each service. When A→B is present but B→A is not,
 * synthesize the reverse entry with the same value (backend contract:
 * A→B implies B→A).
 */
export function buildTravelTimeMap(
  services: TourEligibleServiceDao[] | null | undefined
): TravelTimeMap {
  const map = new Map<string, number>();
  if (!Array.isArray(services)) return map;
  for (const service of services) {
    if (!service.travel_times) continue;
    for (const row of service.travel_times) {
      const forwardKey = `${row.from_service_id}-${row.to_service_id}`;
      map.set(forwardKey, row.travel_time_minutes);
    }
  }
  // Synthesize missing reverse entries
  const synthesized = new Map<string, number>();
  for (const [key, value] of map.entries()) {
    const [from, to] = key.split('-').map(Number);
    const reverseKey = `${to}-${from}`;
    if (!map.has(reverseKey)) {
      synthesized.set(reverseKey, value);
    }
  }
  for (const [k, v] of synthesized.entries()) {
    map.set(k, v);
  }
  return map;
}

/**
 * Compute the total visit time (sum of duration + lead + followup across all
 * selected services). Duration from the backend is in HOURS (float); other
 * fields are in minutes.
 */
export function computeVisitMinutes(
  selected: TourEligibleServiceDao[]
): number {
  return selected.reduce((sum, s) => {
    const durationMin = Math.round((Number(s.duration) || 0) * 60);
    const lead = Number(s.lead_time) || 0;
    const followup = Number(s.followup_time) || 0;
    return sum + durationMin + lead + followup;
  }, 0);
}

/**
 * Compute total travel time between consecutive services in the given order.
 * Returns 0 when fewer than 2 services are selected or no matching row.
 */
export function computeTravelMinutes(
  orderedSelected: TourEligibleServiceDao[],
  travelMap: TravelTimeMap
): number {
  if (orderedSelected.length < 2) return 0;
  let total = 0;
  for (let i = 0; i < orderedSelected.length - 1; i++) {
    const key = `${orderedSelected[i].id}-${orderedSelected[i + 1].id}`;
    total += travelMap.get(key) ?? 0;
  }
  return total;
}

/**
 * For each adjacent pair of categories (plants) in the given ordered category
 * list — both of which have at least one selected service — compute the
 * travel time between last-selected-of-A and first-selected-of-B.
 * Used to render the inter-plant travel pills between card groups.
 *
 * Returns a map keyed by "${fromCategoryId}-${toCategoryId}" → minutes.
 */
export function computePlantTravelPills(
  orderedCategories: { id: number; services: TourEligibleServiceDao[] }[],
  selectedIds: Set<number>,
  travelMap: TravelTimeMap
): Map<string, number> {
  const result = new Map<string, number>();
  for (let i = 0; i < orderedCategories.length - 1; i++) {
    const fromCat = orderedCategories[i];
    const toCat = orderedCategories[i + 1];
    const fromSelected = fromCat.services.filter((s) => selectedIds.has(s.id));
    const toSelected = toCat.services.filter((s) => selectedIds.has(s.id));
    if (fromSelected.length === 0 || toSelected.length === 0) continue;
    const lastOfFrom = fromSelected[fromSelected.length - 1];
    const firstOfTo = toSelected[0];
    const minutes =
      travelMap.get(`${lastOfFrom.id}-${firstOfTo.id}`) ?? 0;
    result.set(`${fromCat.id}-${toCat.id}`, minutes);
  }
  return result;
}
